import React, { useState } from 'react';
import { Users, AlertCircle } from 'lucide-react';
import { formatPhoneNumber, formatZipCode } from '../../utils/formatting';

interface Step2Props {
  formData: any;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  sameAsProperty: boolean;
  onSameAsPropertyChange: (checked: boolean) => void;
}

export function Step2OwnerContact({ formData, onChange, sameAsProperty, onSameAsPropertyChange }: Step2Props) {
  const [emailError, setEmailError] = useState<string>('');

  // Mailing fields are locked (read-only) while "Same as property address" is checked.
  const mailingFieldClass = (locked: boolean) =>
    `w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all ${
      locked ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed' : 'border-gray-200'
    }`;

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const email = e.target.value.trim();
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailError(''); // Clear error as user types
    onChange(e);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    onChange({
      ...e,
      target: {
        ...e.target,
        name: e.target.name,
        value: formatted
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatZipCode(e.target.value);
    onChange({
      ...e,
      target: {
        ...e.target,
        name: e.target.name,
        value: formatted
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-tdb-primary mb-6 flex items-center space-x-2">
        <Users className="w-5 h-5 text-tdb-accent" />
        <span>Owner & Contact Information</span>
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Owner First Name *
          </label>
          <p className="text-xs text-gray-500 mb-2">Property Owner of Record</p>
          <input
            type="text"
            name="ownerFirstName"
            value={formData.ownerFirstName}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Owner Last Name *
          </label>
          <p className="text-xs text-gray-500 mb-2">Property Owner of Record</p>
          <input
            type="text"
            name="ownerLastName"
            value={formData.ownerLastName}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Owner Phone Number *
          </label>
          <p className="text-xs text-gray-500 mb-2">Best phone number to reach the homeowner</p>
          <input
            type="tel"
            name="ownerPhone"
            value={formData.ownerPhone}
            onChange={handlePhoneChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="(555) 123-4567"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Owner Email *
          </label>
          <p className="text-xs text-gray-500 mb-2">Best email for important documentation</p>
          <input
            type="email"
            name="ownerEmail"
            value={formData.ownerEmail}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            required
            className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
              emailError
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-200 focus:ring-tdb-accent focus:border-tdb-accent'
            }`}
          />
          {emailError && (
            <div className="flex items-center space-x-2 mt-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{emailError}</span>
            </div>
          )}
        </div>

        <div className="md:col-span-2 pt-2">
          <label
            className={`inline-flex items-center gap-2 ${
              formData.propertyStreet ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
            }`}
          >
            <input
              type="checkbox"
              checked={sameAsProperty}
              disabled={!formData.propertyStreet}
              onChange={(e) => onSameAsPropertyChange(e.target.checked)}
              className="w-4 h-4 rounded border-2 border-gray-300 text-tdb-accent focus:ring-tdb-accent disabled:cursor-not-allowed"
            />
            <span className="text-sm font-semibold text-tdb-primary">Same as property address</span>
          </label>
          {!formData.propertyStreet && (
            <p className="text-xs text-gray-400 mt-1">Please enter the property address first.</p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Mailing Address - Street *
          </label>
          <input
            type="text"
            name="mailingStreet"
            value={formData.mailingStreet}
            onChange={onChange}
            required
            disabled={sameAsProperty}
            className={mailingFieldClass(sameAsProperty)}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Mailing Address - Line 2
          </label>
          <input
            type="text"
            name="mailingLine2"
            value={formData.mailingLine2}
            onChange={onChange}
            disabled={sameAsProperty}
            className={mailingFieldClass(sameAsProperty)}
            placeholder="Apt, Suite, etc."
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            City *
          </label>
          <input
            type="text"
            name="mailingCity"
            value={formData.mailingCity}
            onChange={onChange}
            required
            disabled={sameAsProperty}
            className={mailingFieldClass(sameAsProperty)}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            State *
          </label>
          <input
            type="text"
            name="mailingState"
            value={formData.mailingState}
            onChange={onChange}
            required
            maxLength={2}
            disabled={sameAsProperty}
            className={mailingFieldClass(sameAsProperty)}
            placeholder="FL"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Zip Code *
          </label>
          <input
            type="text"
            name="mailingZip"
            value={formData.mailingZip}
            onChange={handleZipChange}
            required
            maxLength={10}
            disabled={sameAsProperty}
            className={mailingFieldClass(sameAsProperty)}
            placeholder="12345 or A1A 1A1"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Best Way to Contact *
          </label>
          <select
            name="bestContactMethod"
            value={formData.bestContactMethod}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          >
            <option value="">Choose One...</option>
            <option value="phone">Phone Call</option>
            <option value="text">Text Message</option>
            <option value="email">Email</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Best Time to Contact *
          </label>
          <select
            name="bestContactTime"
            value={formData.bestContactTime}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          >
            <option value="">Choose One...</option>
            <option value="morning">8:00 AM to 11:00 AM EST</option>
            <option value="afternoon">12:00 PM to 5:00 PM EST</option>
            <option value="evening">6:00 PM to 8:00 PM EST</option>
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            What option do you see going with for this property? (Deal Type) *
          </label>
          <select
            name="dealType"
            value={formData.dealType}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          >
            <option value="">Choose One...</option>
            <option value="cash-offer">Standing Cash Offer (Wholesale)</option>
            <option value="lease-option">Lease With Option to Purchase</option>
            <option value="rent-back">Rent-Back With Option to Purchase</option>
            <option value="undecided">I don't know yet</option>
          </select>
        </div>
      </div>
    </div>
  );
}
